import { BlobServiceClient } from '@azure/storage-blob';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const FILE_SIGNATURES = {
  jpg: [[0xff, 0xd8, 0xff]],
  jpeg: [[0xff, 0xd8, 0xff]],
  png: [[0x89, 0x50, 0x4e, 0x47]],
  gif: [[0x47, 0x49, 0x46, 0x38]],
  pdf: [[0x25, 0x50, 0x44, 0x46]], // '%PDF'
  docx: [[0x50, 0x4b, 0x03, 0x04]],
  xlsx: [[0x50, 0x4b, 0x03, 0x04]],
};

const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'pdf', 'docx', 'xlsx'];

const EXTENSION_MIME_MAP = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

/**
 * Checks the first few bytes of the file against known file signatures.
 */
async function checkFileSignature(file, extension) {
  if (!FILE_SIGNATURES[extension]) return true;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target.result);
      const signatureSets = FILE_SIGNATURES[extension];
      const isMatch = signatureSets.some((sigPattern) =>
        sigPattern.every((byte, index) => arr[index] === byte),
      );
      resolve(isMatch);
    };
    reader.onerror = (err) => reject(err);
    // Read the first 8 bytes
    reader.readAsArrayBuffer(file.slice(0, 8));
  });
}

async function checkPdfForEmbeds(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      // Convert the data to string
      const text = new Uint8Array(e.target.result);

      let fileContent;
      try {
        fileContent = new TextDecoder().decode(text);
      } catch (err) {
        return reject(new Error('Failed to decode PDF file for scanning.'));
      }

      // Look for suspicious markers
      const suspiciousMarkers = [
        '/EmbeddedFile', // typical marker for embedded attachments
        '/EmbeddedFiles',
        '/JS',           // PDFs can contain JavaScript
        '/JavaScript',
      ];

      const foundSuspicious = suspiciousMarkers.some((marker) =>
        fileContent.includes(marker),
      );
      resolve(!foundSuspicious); // If suspicious, return false
    };

    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });
}

// <----------------------------------- Main Azure Upload Function ----------------------------------->
export async function AWSUpload(file) {
  try {
    const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File size exceeds 2MB limit');
      return;
    }

    const fileName = file.name;
    // 1. Check for double extension like ".docx.exe"
    const invalidExtensionPattern = /\.\w+\.\w{3,4}$/i;
    if (invalidExtensionPattern.test(fileName)) {
      toast.error('Files with multiple extensions are not allowed.');
      return;
    }

    // 2. Check allowed extensions
    const extension = fileName.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      toast.error(`File type not allowed: .${extension}`);
      return;
    }

    // 3. Check MIME type (if available)
    const expectedMimeType = EXTENSION_MIME_MAP[extension];
    if (expectedMimeType && file.type && file.type !== expectedMimeType) {
      toast.error(`MIME type mismatch. Expected ${expectedMimeType} but got ${file.type}`);
      return;
    }

    // 4. Check file signature
    const isValidSignature = await checkFileSignature(file, extension);
    if (!isValidSignature) {
      toast.error(`File content does not match the expected format for .${extension}`);
      return;
    }

    // 5. If PDF, check for embeds
    if (extension === 'pdf') {
      const isCleanPdf = await checkPdfForEmbeds(file);
      if (!isCleanPdf) {
        toast.error(`PDF contains embedded files or scripts. Upload not allowed.`);
        return;
      }
    }

    // <--------------- Azure Blob Storage Section --------------->

    const sasUrl = "https://odishminingblobsg.blob.core.windows.net/blobcontainer-sg?sp=racwd&st=2025-11-05T04:53:36Z&se=2027-01-01T13:08:36Z&spr=https&sv=2024-11-04&sr=c&sig=hQ%2BFCGxIoCVboFWlW7S%2Bp2BYyN2m%2FfAIYtu41mcHoPI%3D";
    const containerName = "blobcontainer-sg"; // Your container name

    // Create BlobServiceClient from SAS URL (browser-safe)
    const blobServiceClient = new BlobServiceClient(sasUrl);

    // Get the container client
    const containerClient = blobServiceClient.getContainerClient(containerName);

    // Generate a random file name (to avoid collisions)
    const randomName = `${Math.random().toString(36).substring(6)}.${extension}`;

    // Get blockBlobClient
    const blockBlobClient = containerClient.getBlockBlobClient(randomName);

    // Upload the file
    await blockBlobClient.uploadData(file, {
      blobHTTPHeaders: { blobContentType: file.type || expectedMimeType }
    });

    toast.success(`File Uploaded successfully!`);
    return blockBlobClient.url;

  } catch (error) {
    toast.error(`Error during upload`);
    console.error('Error during upload:', error.message || error);
    return;
  }
}